import type { SetupMangoPayDirectDebitResidentRequest } from '@pflegenavi/frontend/api-nursing-home';
import {
  useCancelFamilyMemberMandate,
  useCancelResidentMandate,
  useGetFamilyMemberResident,
  useGetGetFamilyMemberCountries,
  useGetMangoPayDirectDebitResident,
  useSetupMangoPayDirectDebitResident,
  useShowResidentForFamilyMemberPhoenix,
  useUpdateFamilyMemberResident,
  useGetPaymentInfoForFamilyMemberPhoenix,
  useResidentBalanceInfo,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  getActiveBalanceId,
  getSelectedBalance,
} from '@pflegenavi/shared-frontend/platform';
import {
  MangopayBankAccountOwner,
  PaymentInitiatedBy,
  PendingChargeStatus,
  ResidentState,
  SourceOfMandate,
} from '@pflegenavi/shared/api';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitializeFM } from '../hooks/useInitializeFM';
import { MangopayDirectDebitPageView } from './MangopayDirectDebitPageView';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface MangopayDirectDebitPageProps {}

export const MangopayDirectDebitPage: FC<MangopayDirectDebitPageProps> = () => {
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();
  const { enqueueSnackbar } = useSnackbar();

  // State for modals
  const [showSwitchBankAccountModal, setShowSwitchBankAccountModal] =
    useState(false);

  const { data: countries } = useGetGetFamilyMemberCountries({
    tenant: selectedResident?.tenant,
  });

  const {
    data: residentFamilyMember,
    isLoading: isResidentFamilyMemberLoading,
  } = useGetFamilyMemberResident({
    tenant: selectedResident?.tenant ?? '',
    resident_id: selectedResident?.residentId ?? '',
  });

  const { mutateAsync: updateFamilyMemberResident } =
    useUpdateFamilyMemberResident({
      resident_id: selectedResident?.residentId ?? '',
      tenant: selectedResident?.tenant ?? '',
    });

  const { data: residentMandate, isLoading: isResidentMandateLoading } =
    useGetMangoPayDirectDebitResident({
      paymentProcessorMode:
        selectedResident?.tenantPaymentProcessorMode ?? 'live',
      tenant: selectedResident?.tenant ?? '',
      resident_id: selectedResident?.residentId ?? '',
    });

  const { mutateAsync: setupDirectDebitResident } =
    useSetupMangoPayDirectDebitResident();

  const { data: residentResultPhoenix, isPending: isResidentPending } =
    useShowResidentForFamilyMemberPhoenix(
      selectedResident?.residentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const { data: balanceInfo, isPending: isBalanceInfoPending } =
    useResidentBalanceInfo({
      residentId: selectedResident?.residentId ?? '',
    });

  const activeBalance = useMemo(() => {
    const activeBalanceId = getActiveBalanceId(
      residentResultPhoenix?.data.resident
    );
    return getSelectedBalance(residentResultPhoenix?.data, activeBalanceId);
  }, [residentResultPhoenix]);

  // Check resident states for disabling functionality
  const isResidentExited =
    residentResultPhoenix?.data.resident.state === ResidentState.exited;
  const isResidentArchived =
    residentResultPhoenix?.data.resident.state === ResidentState.archived;
  const isResidentAwaitingSettlement =
    residentResultPhoenix?.data.resident.state ===
    ResidentState.awaitingSettlement;

  // Determine what to disable based on resident state
  const isPageDisabled = isResidentArchived || isResidentAwaitingSettlement;
  const isSepaFormDisabled = isResidentExited;

  // Fetch payment info to check for failed payments
  const { data: paymentInfoPhoenix } = useGetPaymentInfoForFamilyMemberPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  // Check if there's a failed/frozen charge initiated by family member
  const hasFailedPayment = useMemo(() => {
    const pendingCharges = paymentInfoPhoenix?.pending_charges ?? [];
    return pendingCharges.some(
      (charge) =>
        (charge.pending_charge_status === PendingChargeStatus.failed ||
          charge.pending_charge_status === PendingChargeStatus.frozen) &&
        charge.payment_initiation === PaymentInitiatedBy.FamilyMember
    );
  }, [paymentInfoPhoenix?.pending_charges]);

  // Check if there's a pending payment from family member/resident source (blocks mandate cancellation)
  const hasPendingFamilyPayment = useMemo(() => {
    const pendingCharges = paymentInfoPhoenix?.pending_charges ?? [];
    return pendingCharges.some(
      (charge) =>
        charge.pending_charge_status === PendingChargeStatus.pending &&
        (charge.source_of_mandate === SourceOfMandate.Resident ||
          charge.source_of_mandate === SourceOfMandate.FamilyMember)
    );
  }, [paymentInfoPhoenix?.pending_charges]);

  // Get payment initiation type
  const paymentInitiation =
    paymentInfoPhoenix?.payment_settings?.payment_initiation;

  // Check if manual payments are disabled
  const isPaymentDisabled = paymentInitiation === PaymentInitiatedBy.Disabled;
  // Always use resident mandate
  const mandate = residentMandate;

  const confirmOwnerChange = useCallback(() => {
    // Always use 'resident' as the source of mandate
    return updateFamilyMemberResident({
      source_of_mandate: 'resident',
    }).then(() => {
      enqueueSnackbar(
        t(
          'mangopay.direct-debit.setup.bank-account-owner.owner-change-success'
        ),
        { variant: 'success' }
      );
      setShowSwitchBankAccountModal(false);
      return undefined;
    });
  }, [enqueueSnackbar, t, updateFamilyMemberResident]);

  const cancelOwnerChange = useCallback(() => {
    setShowSwitchBankAccountModal(false);
  }, []);

  const setupMandate = useCallback(
    (body: SetupMangoPayDirectDebitResidentRequest['body']) => {
      return setupDirectDebitResident({
        body: body,
        params: {
          tenant: selectedResident?.tenant ?? '',
          resident_id: selectedResident?.residentId ?? '',
          paymentProcessorMode:
            selectedResident?.tenantPaymentProcessorMode ?? 'live',
        },
      });
    },
    [
      selectedResident?.residentId,
      selectedResident?.tenant,
      selectedResident?.tenantPaymentProcessorMode,
      setupDirectDebitResident,
    ]
  );

  const mandateActive = mandate?.data?.mandate?.status === 'active';
  const mandatePending = mandate?.data?.mandate?.status === 'pending';
  const mandateDocumentUrl = mandate?.data?.mandate?.document_url;

  const isLoading = isResidentFamilyMemberLoading || isResidentMandateLoading;

  // Always set owner to Resident
  const owner = MangopayBankAccountOwner.Resident;

  // Update resident family member to use 'resident' as source of mandate if it's not already set
  React.useEffect(() => {
    if (residentFamilyMember?.data?.source_of_mandate !== 'resident') {
      updateFamilyMemberResident({ source_of_mandate: 'resident' });
    }
  }, [
    residentFamilyMember?.data?.source_of_mandate,
    updateFamilyMemberResident,
  ]);

  const isFamilyMemberOwner = false; // Always false since owner is always Resident

  // Hook reference for CancelDirectDebitMangopay component
  const useDeleteDirectDebitMandate = (
    residentFamilyMember?.data?.source_of_mandate === 'resident'
      ? useCancelResidentMandate
      : useCancelFamilyMemberMandate
  ) as typeof useCancelResidentMandate;

  // Cancel mutation for pending mandate
  const cancelResidentMandateMutation = useCancelResidentMandate();
  const cancelFamilyMemberMandateMutation = useCancelFamilyMemberMandate();

  const onCancelMandate = useCallback(() => {
    const cancelMutation =
      residentFamilyMember?.data?.source_of_mandate === 'resident'
        ? cancelResidentMandateMutation
        : cancelFamilyMemberMandateMutation;

    cancelMutation.mutate(
      {
        paymentProcessorMode:
          selectedResident?.tenantPaymentProcessorMode ?? 'live',
        resident_id: selectedResident?.residentId ?? '',
        tenant: selectedResident?.tenant ?? '',
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('direct-debit.cancel-success'), {
            variant: 'success',
          });
        },
        onError: () => {
          enqueueSnackbar(t('direct-debit.cancel-error'), { variant: 'error' });
        },
      }
    );
  }, [
    residentFamilyMember?.data?.source_of_mandate,
    cancelResidentMandateMutation,
    cancelFamilyMemberMandateMutation,
    selectedResident?.tenantPaymentProcessorMode,
    selectedResident?.residentId,
    selectedResident?.tenant,
    enqueueSnackbar,
    t,
  ]);

  return (
    <MangopayDirectDebitPageView
      countries={countries?.data ?? []}
      mandate={mandate}
      activeBalance={activeBalance}
      balanceInfo={balanceInfo}
      isLoading={isLoading}
      isResidentPending={isResidentPending}
      isBalanceInfoLoading={isBalanceInfoPending}
      mandateActive={mandateActive}
      mandatePending={mandatePending}
      mandateDocumentUrl={mandateDocumentUrl}
      owner={owner}
      isFamilyMemberOwner={isFamilyMemberOwner}
      setupMandate={setupMandate}
      confirmOwnerChange={confirmOwnerChange}
      cancelOwnerChange={cancelOwnerChange}
      onCancelMandate={onCancelMandate}
      showSwitchBankAccountModal={showSwitchBankAccountModal}
      useDeleteDirectDebitMandate={useDeleteDirectDebitMandate}
      hasFailedPayment={hasFailedPayment}
      hasPendingFamilyPayment={hasPendingFamilyPayment}
      isPageDisabled={isPageDisabled}
      isSepaFormDisabled={isSepaFormDisabled}
      paymentInitiation={paymentInitiation}
      isPaymentDisabled={isPaymentDisabled}
    />
  );
};
